import React, { useEffect, useState, useRef, useMemo } from "react";
import {Button, Typography, message, Space} from "antd";
import ScatterChart from "../charts/ModalCharts/ZscoreChart";
import LocalOutlierScatterChart from "../charts/ModalCharts/LocalOutlierChart";
import RsfChart from "../charts/ModalCharts/RsfChart";
import BenfordChart from '../charts/ModalCharts/BenfordChart'
import PdfChart from "../charts/ModalCharts/PdfChart";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
 
const { Text } = Typography;
 
// Utility function to convert names for display
const convertToDisplayName = (name) => {
  if (!name) return "";
  // Convert "Unit_Cost" -> "Unit Cost", "Item_Branch" -> "Item Branch"
  return name.replace(/_/g, " ");
};
 
// Utility function to check if backend name matches data name
const namesMatch = (backendName, dataName) => {
  if (!backendName || !dataName) return false;
  const backendNormalized = backendName.replace(/\s+/g, "_");
  return backendNormalized === dataName;
};
 
const AnalyticsDashboard = ({ applyResponse }) => {
  const navigate = useNavigate();
  const [processedData, setProcessedData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isConfigModalVisible, setIsConfigModalVisible] = useState(false);
 
  // User-selected values (ALWAYS use data format with underscores)
  const [userDepVariable, setUserDepVariable] = useState(null);
  const [userGroupByColumns, setUserGroupByColumns] = useState([]);
 
  const wsRef = useRef(null);
  const wsUrl =
    "ws://172.203.236.174:9000/data_models_machine_learning/ws/staging-data";
 
  // Initialize - but don't force backend values, just suggest them
  useEffect(() => {
    if (!applyResponse) return;
 
    const backendDep =
      applyResponse.dependent_column || applyResponse.dep_variable || null;
    const backendGroupBy = Array.isArray(applyResponse.grp_by_columns)
      ? applyResponse.grp_by_columns
      : Array.isArray(applyResponse.groupby_columns)
      ? applyResponse.groupby_columns
      : [];
 
    // console.log("Backend suggested configuration:", {
    //   backendDep,
    //   backendGroupBy,
    // });
 
    // Only set initial user selections if they haven't been set yet
    // This allows users to change freely after initial load
    // console.log("Current user selections:", {
    //   userDepVariable,
    //   backendDep,
    // });
    if (!userDepVariable && backendDep) {
      // Try to find matching column in available data
      // console.log("Available columns for matching:", availableColumns, "backendDep", backendDep);
      const suggestedDep = findMatchingColumn(backendDep, availableColumns);
      // console.log("Suggested dependent variable:", suggestedDep);
      if (suggestedDep) {
        setUserDepVariable(suggestedDep);
        // console.log("Set initial dependent variable to:", suggestedDep);
      }
    }
    
    // console.log("Current user group by selections:", {
    //   userGroupByColumns,
    //   backendGroupBy,
    // });
    if (userGroupByColumns.length === 0 && backendGroupBy.length > 0) {
      const suggestedGroupBy = backendGroupBy
        .map((col) => findMatchingColumn(col, availableColumns))
        .filter(Boolean);
      if (suggestedGroupBy.length > 0) {
        setUserGroupByColumns(suggestedGroupBy);
        // console.log("Set initial group by to:", suggestedGroupBy);
      }
    }
 
    setIsInitialLoad(false);
  }, [applyResponse, processedData]); // Add processedData to refresh when data arrives
 
  // Helper function to find matching column
  const findMatchingColumn = (backendName, availableColumns) => {
    if (!backendName || !availableColumns.length) return null;
 
    const backendNormalized = backendName.replace(/\s+/g, "_");
    // console.log("Finding match for backend name:", backendName, "normalized:", backendNormalized);
 
    // Exact match
    const exactMatch = availableColumns.find((col) => {
      const normalizedCol = col.replace(/_/g, "_");
      const normalizedBackend = backendNormalized.replace(/__+/g, "_"); 
      // console.log("Comparing:", normalizedCol, "vs", normalizedBackend);
      return normalizedCol === normalizedBackend;
    });

    // console.log("Exact match result:", exactMatch);
    if (exactMatch) return exactMatch;
 
    // Case-insensitive match
    const caseInsensitiveMatch = availableColumns.find(
      (col) => col.toLowerCase() === backendNormalized.toLowerCase()
    );
    // console.log("Case-insensitive match result:", caseInsensitiveMatch);
    if (caseInsensitiveMatch) return caseInsensitiveMatch;
 
    // Contains match
    const containsMatch = availableColumns.find(
      (col) =>
        col.toLowerCase().includes(backendNormalized.toLowerCase()) ||
        backendNormalized.toLowerCase().includes(col.toLowerCase())
    );
    // console.log("Contains match result:", containsMatch);
 
    return containsMatch || null;
  };
 
  // Use user selections (user has full control)
  const effectiveDepVariable = userDepVariable;
  const effectiveGroupByColumns = userGroupByColumns;
 
  // WebSocket connection
  useEffect(() => {
    if (!applyResponse?.blob_download_link) return;
 
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
 
    ws.onopen = () => {
      // console.log("WebSocket connection established", wsUrl);
      message.success("Connected to real-time data stream");
       // Compute backend-suggested fields in-scope for this effect
      //  const backendDep =
      //    applyResponse?.dependent_column || applyResponse?.dep_variable || null;
      //  const backendGroupBy = Array.isArray(applyResponse?.grp_by_columns)
      //    ? applyResponse.grp_by_columns
      //    : Array.isArray(applyResponse?.groupby_columns)
      //    ? applyResponse.groupby_columns
      //    : [];

      //  const payload = {
      //    dependent_variable: backendDep,
      //    group_by: backendGroupBy,
      //  };

      //  ws.send(JSON.stringify(payload));
      //  console.log("Sent initial payload:", payload);
    };
 
    ws.onmessage = (event) => {
      try {
        const msgData = JSON.parse(event.data);
        // console.log("WebSocket message received:", msgData);
 
        if (msgData.status === "success" && Array.isArray(msgData.data)) {
          setChartData(msgData.data);
          setProcessedData((prev) => {
            const newData = [...prev, ...msgData.data];
            // console.log("Total processed data:", newData.length);
            return newData;
          });
 
          if (isInitialLoad) {
            // message.info(
            //   `Receiving data... ${msgData.data.length} records processed`
            // );
            setIsInitialLoad(false);
          }
        } else if (msgData.status === "complete") {
          // console.log("WebSocket complete");
          message.success("Data processing completed");
        } else if (msgData.error) {
          console.error("WebSocket error:", msgData.error);
          // message.error(`Data error: ${msgData.error}`);
        }
      } catch (err) {
        console.error("Error parsing WS message:", err);
      }
    };
 
    ws.onerror = (e) => {
      // console.error("WebSocket error", e);
      // message.error("WebSocket connection error");
    };
 
    ws.onclose = () => console.log("WebSocket closed");
 
    return () => {
      try {
        if (wsRef.current) {
          wsRef.current.close();
        }
      } catch (e) {
        console.error("Error closing WebSocket:", e);
      }
      wsRef.current = null;
    };
  }, [applyResponse?.blob_download_link]);
 
  // Get available columns from actual data (with underscores)
  const availableColumns = useMemo(() => {
    // console.log("Extracting available columns from data...", processedData); 
    if (processedData.length > 0) {
      const dataColumns = Object.keys(processedData[0]);
      // console.log("Available columns from data:", dataColumns);
      return dataColumns;
    }
 
    // If no data yet, return empty array
    return [];
  }, [processedData]);
 
  // Get numeric columns from actual data
  const numericColumns = useMemo(() => {
    // console.log("Detecting numeric columns from data...");
 
    if (processedData.length === 0) {
      // console.log("No data available for numeric detection");
      return [];
    }
 
    const sampleRow = processedData[0];
    const numericCols = Object.keys(sampleRow).filter((col) => {
      const val = sampleRow[col];
 
      if (val === null || val === undefined) return false;
 
      if (typeof val === "number" && Number.isFinite(val)) {
        return true;
      }
 
      if (typeof val === "string") {
        const cleanVal = val.replace(/,/g, "").trim();
        const parsed = parseFloat(cleanVal);
        return !Number.isNaN(parsed) && isFinite(parsed);
      }
 
      return false;
    });
 
    // console.log("Detected numeric columns:", numericCols);
    return numericCols;
  }, [processedData]);
 
  // Prepare scatter data - uses whatever user selected
  const scatterData = useMemo(() => {
    // console.log("Preparing scatter data...");
    // console.log("User selected Dep Variable:", effectiveDepVariable);
    // console.log("User selected Group By:", effectiveGroupByColumns);
    // console.log("Processed data length:", processedData.length);
 
    if (!effectiveDepVariable || processedData.length === 0) {
      // console.log("No dependent variable selected or no data available");
      return [];
    }
 
    // Check if selected dependent variable exists
    if (!availableColumns.includes(effectiveDepVariable)) {
      console.warn(
        `Selected dependent variable '${effectiveDepVariable}' not found in data`
      );
      return [];
    }
 
    const useGroupBy = effectiveGroupByColumns.length > 0;
    const categories = [];
    const groupToIndex = {};
 
    // Validate all selected group by columns exist
    if (useGroupBy) {
      const missingColumns = effectiveGroupByColumns.filter(
        (col) => !availableColumns.includes(col)
      );
      if (missingColumns.length > 0) {
        console.warn(`Some group by columns not found:`, missingColumns);
        // Continue with available columns only
        effectiveGroupByColumns = effectiveGroupByColumns.filter((col) =>
          availableColumns.includes(col)
        );
      }
 
      // Collect unique groups
      const groupSet = new Set();
      processedData.forEach((row) => {
        const keyParts = effectiveGroupByColumns.map((col) => {
          const raw = row[col];
          return raw !== undefined && raw !== null ? String(raw) : "N/A";
        });
        const groupKey = keyParts.join(" | ");
        groupSet.add(groupKey);
      });
 
      categories.push(...Array.from(groupSet).sort());
      categories.forEach((cat, idx) => {
        groupToIndex[cat] = idx;
      });
    }
 
    const scatterPoints = processedData
      .map((row, index) => {
        try {
          // Get dependent variable value
          const rawY = row[effectiveDepVariable];
          let y;
 
          if (typeof rawY === "number") {
            y = rawY;
          } else if (typeof rawY === "string") {
            y = parseFloat(rawY.replace(/,/g, ""));
          } else {
            y = NaN;
          }
 
          if (Number.isNaN(y)) {
            console.warn(
              `Invalid numeric value for ${effectiveDepVariable}:`,
              rawY
            );
            return null;
          }
 
          // Detect anomalies
          let exception = 0;
          const zScoreField =
            row.Z_SCORE || row.z_score || row["Z-SCORE"] || row.anomaly_score;
          const outlierField =
            row.Outlier_ZScore ||
            row.outlier_zscore ||
            row.is_anomaly ||
            row.anomaly;
 
          if (outlierField !== undefined) {
            exception = parseInt(outlierField) === 1 ? 1 : 0;
          } else if (zScoreField !== undefined) {
            const z = Math.abs(parseFloat(zScoreField));
            exception = z > 3 ? 1 : 0;
          }
 
          let xIndex, groupLabel;
          if (useGroupBy && effectiveGroupByColumns.length > 0) {
            const keyParts = effectiveGroupByColumns.map((col) => {
              const raw = row[col];
              return raw !== undefined && raw !== null ? String(raw) : "N/A";
            });
            groupLabel = keyParts.join(" | ");
            xIndex = groupToIndex[groupLabel];
 
            // Add jitter to prevent overlapping
            const jitter = (Math.random() - 0.5) * 0.6;
            return {
              x: xIndex + jitter,
              y: y,
              exception: exception,
              zScore: zScoreField || 0,
              group: groupLabel,
              row: row,
              categories: categories,
            };
          } else {
            // No grouping - use row index
            return {
              x: index + 1,
              y: y,
              exception: exception,
              zScore: zScoreField || 0,
              group: `Row ${index + 1}`,
              row: row,
              categories: [],
            };
          }
        } catch (error) {
          console.error("Error processing row:", error, row);
          return null;
        }
      })
      .filter(Boolean);
 
    // console.log(`Generated ${scatterPoints.length} scatter points`);
    return scatterPoints;
  }, [
    processedData,
    effectiveDepVariable,
    effectiveGroupByColumns,
    availableColumns,
  ]);
 
  // Chart render logic
  const renderChart = () => {
    // console.log("Rendering chart with data:", scatterData.length);
 
    if (!scatterData || scatterData.length === 0) {
      return (
        <div className="text-white text-center p-8">
          <div className="text-lg mb-2">
            {effectiveDepVariable
              ? `Processing "${convertToDisplayName(
                  effectiveDepVariable
                )}" data... (${processedData.length} records loaded)`
              : "🕰️ One moment — polishing your chart gems..."}
          </div>
        </div>
      );
    }
 
    // Read models robustly from applied_models OR models
    const rawModels =
      Array.isArray(applyResponse?.applied_models) &&
      applyResponse.applied_models.length > 0
        ? applyResponse.applied_models
        : Array.isArray(applyResponse?.models)
        ? applyResponse.models
        : [];
 
    const models = rawModels.map((m) => String(m).toUpperCase());
    // console.log("Detected backend models:", models);
 
    const isLOF =
      models.includes("LOF") ||
      models.includes("LOCAL_OUTLIER") ||
      models.includes("LOF_ANOMALY");
    const isRSF =
      models.includes("RSF") ||
      models.includes("RANDOM_SURVEST_FOREST") ||
      models.includes("RANDOM_SURVEST");
    const isBenford =
      models.includes("BENFORD") || models.includes("BENFORD_ANALYSIS");
    const isPDF = models.includes("PDF") || models.includes("PDF_MODEL") || models.includes("PROBABILITY_DENSITY") || models.includes("pdf");
 
    if (isLOF) {
      // console.log(
      //   "Routing to LocalOutlierScatterChart — scatterData length:",
      //   scatterData.length
      // );
      // if (scatterData.length > 0) console.log("sample point:", scatterData[0]);
      return (
        <LocalOutlierScatterChart
          data={scatterData}
          dep={effectiveDepVariable}
          groupBy={effectiveGroupByColumns}
        />
      );
    }
 
    if (isRSF) {
      // console.log(
      //   "Routing to RsfChart — scatterData length:",
      //   scatterData.length
      // );
      // if (scatterData.length > 0) console.log("sample point:", scatterData[0]);
      return (
        <RsfChart
          data={scatterData}
          dep={effectiveDepVariable}
          groupBy={effectiveGroupByColumns}
        />
      );
    }
 
    if (isBenford) {
      // console.log(
      //   "Routing to BenfordChart — scatterData length:",
      //   scatterData.length
      // );
      // if (scatterData.length > 0) console.log("sample point:", scatterData[0]);
 
      // The BenfordChart expects an array of rows with fields:
      // { Actual, Actualvalue, Benford, ... } — map scatterData -> rows
      const benfordPayload = scatterData.map((pt) => {
        // prefer pt.row (raw server row) if present; otherwise pt itself
        const row = pt.row ?? pt;
        return {
          Actual: row.Actual ?? row.actual ?? row["Actual"] ?? null,
          Actualvalue:
            row.Actualvalue ??
            row.ActualValue ??
            row.actualvalue ??
            row.actualValue ??
            row.Actual_value ??
            row.ActualValue ??
            null,
          Benford: row.Benford ?? row.benford ?? null,
          __raw: row, // keep raw for tooltip if needed
        };
      });
 
      return (
        <BenfordChart
          data={benfordPayload}
          groupLabel={effectiveGroupByColumns.join(" | ") || "Group"}
          valueLabel={convertToDisplayName(effectiveDepVariable) || "Value"}
        />
      );
    }

    if (isPDF) {
      return (
        <PdfChart
          data={scatterData}
          dep={effectiveDepVariable}
          groupBy={effectiveGroupByColumns}
        />
      );
    }
 
    // default: use your existing Z-score scatter chart
    const useGroupBy = effectiveGroupByColumns.length > 0;
    return (
      <ScatterChart
        data={scatterData}
        flipAxes={false}
        useCategories={useGroupBy}
        categories={useGroupBy ? scatterData[0]?.categories || [] : []}
        xLabel={
          useGroupBy
            ? effectiveGroupByColumns.map(convertToDisplayName).join(", ")
            : "Row Index"
        }
        yLabel={convertToDisplayName(effectiveDepVariable) || "Value"}
        valueLabel={convertToDisplayName(effectiveDepVariable) || "Value"}
      />
    );
  };
 
 
  const getCurrentConfigSummary = () => {
    if (!effectiveDepVariable) {
      return "No configuration set - please configure chart";
    }
 
    const displayDepVar = convertToDisplayName(effectiveDepVariable);
    const displayGroupBy = effectiveGroupByColumns
      .map(convertToDisplayName)
      .join(", ");
    // console.log("Current config summary:", {
    //   displayDepVar,
    //   displayGroupBy,
    // });
    return `Dependent: ${displayDepVar} | Group By: ${
      displayGroupBy || "None"
    }`;
  };
 
 
  return (
    <div className="bg-[#141c31] rounded-2xl p-8 md:p-10 shadow-lg">
      <div className="mb-4">
        <Button
          type="text"
          className="font-medium !text-white text-sm sm:!text-xl"
          icon={<ArrowLeft className="w-5 h-5 mt-0.5" />}
          onClick={() => navigate("/dashboard/upload-result")}
        >
          Back to Configuration
        </Button>
      </div>
      <div className="relative text-center mb-8">
        <h1 className="text-3xl lg:text-4xl font-bold text-white">
          Anomaly Detection{" "}
          <span className="bg-gradient-to-r from-blue-500 to-cyan-400 bg-clip-text text-transparent">
            Analytics Dashboard
          </span>
        </h1>
        <p className="text-white mt-2">
          Real-time anomaly detection - Configure your own analysis
        </p>

        <div className="mt-4 p-3 bg-gray-800 rounded-lg border border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <Text className="text-gray-300 text-sm">
              <strong>Current Configuration:</strong>{" "}
              {getCurrentConfigSummary()}
            </Text>
          </div>
        </div>
      </div>

      <div className="mb-8 p-6 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-gray-700 rounded-xl shadow-lg">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div className="text-gray-300">
            <span className="font-semibold text-white">Data Points:</span>{" "}
            {processedData.length}
            {effectiveGroupByColumns.length > 0 && (
              <span className="ml-4">
                <span className="font-semibold text-white">Groups:</span>{" "}
                {
                  new Set(
                    processedData.map((row) =>
                      effectiveGroupByColumns.map((col) => row[col]).join(" | ")
                    )
                  ).size
                }
              </span>
            )}
            <span className="ml-4">
              <span className="font-semibold text-white">Anomalies:</span>{" "}
              {scatterData.filter((d) => d.exception === 1).length}
            </span>
          </div>
          <Button
            onClick={() =>
              window.open(applyResponse?.blob_download_link, "_blank")
            }
            className="w-full sm:w-auto px-5 py-3 sm:px-6 sm:py-2 rounded-full font-medium shadow-md bg-white text-slate-800 border hover:bg-slate-50 transition"
            disabled={!applyResponse?.blob_download_link}
          >
            Download Results
          </Button>
        </div>
      </div>

      <div className="text-white">
        <div className="w-full h-[600px] rounded-xl border border-slate-700/60 bg-slate-800/40">
          {renderChart()}
        </div>
      </div>
    </div>
  );
};
 
export default AnalyticsDashboard;
 
 